'use client'

import { useProductBrand } from '@/providers/product-brand-provider'

import { Cookie } from 'lucide-react'
import Link from 'next/link'
import { useContext, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  ConsentContext,
  useConsent,
} from '@/components/analytics/consent-provider'
import { CONSENT_ALL, CONSENT_NONE } from '@/lib/analytics/consent'

function useCookieProductName() { return useProductBrand().name }

/**
 * Cookie banner and the "Cookie Settings" dialog it can be reopened into.
 *
 * The banner renders only once the provider has read the consent cookie in the
 * browser (`resolved`) and found no decision. It deliberately does NOT trust
 * the server-rendered value: the marketing routes are edge-cached without
 * `Vary: Cookie`, so that value belongs to whoever populated the cache. Gating
 * on `resolved` is what keeps the banner from reappearing for a visitor who has
 * already chosen — the property the previous, localStorage-based implementation
 * lost, and which seeding from the cookie on the server only appeared to fix.
 */
export function CookieConsent() {
  const productName = useCookieProductName()
  const {
    decision,
    resolved,
    regionPending,
    save,
    isSettingsOpen,
    setSettingsOpen,
  } = useConsent()

  // `regionPending` keeps the banner off the screen for the moment it takes to
  // learn the visitor is outside the EEA/UK/CH, where they are never asked. It
  // is a fetch against a dynamic route, so this is a flash, not a wait.
  const showBanner =
    resolved && !regionPending && decision === null && !isSettingsOpen

  return (
    <>
      {showBanner ? (
        <div
          role="region"
          aria-label="Cookie preferences"
          className="animate-in fade-in-0 slide-in-from-bottom-4 border-border/60 bg-background/95 fixed inset-x-4 bottom-4 z-[100] rounded-2xl border p-4 shadow-lg backdrop-blur-xl duration-300 ease-out sm:inset-x-auto sm:end-6 sm:bottom-6 sm:w-[340px]"
        >
          <div className="flex items-center gap-2">
            <Cookie
              aria-hidden
              className="text-muted-foreground size-4 shrink-0"
            />
            <p className="text-foreground text-sm font-medium">
              We use cookies
            </p>
          </div>
          <p className="text-muted-foreground mt-1.5 text-[13px] leading-5">
            Essential cookies keep {productName} running. Optional ones help us measure
            usage and campaigns.{' '}
            <Link
              href="/privacy"
              className="underline underline-offset-2 hover:no-underline"
            >
              Privacy Policy
            </Link>
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => save(CONSENT_ALL)}
            >
              Accept all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => save(CONSENT_NONE)}
            >
              Reject optional
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="text-muted-foreground hover:text-foreground mt-3 w-full text-center text-xs underline underline-offset-2 transition-colors hover:no-underline"
          >
            Manage choices
          </button>
        </div>
      ) : null}

      <CookieSettingsDialog
        open={isSettingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </>
  )
}

function CookieSettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie settings</DialogTitle>
          <DialogDescription>
            Essential cookies are always on — they are required for
            authentication, security, delivery, and preferences. Everything else
            is your choice.
          </DialogDescription>
        </DialogHeader>

        {/* DialogContent unmounts when closed, so the toggles below always
            initialize from what is actually stored — reopening never shows a
            stale choice left behind by a previous cancel. */}
        <CookieSettingsForm />
      </DialogContent>
    </Dialog>
  )
}

function CookieSettingsForm() {
  const productName = useCookieProductName()
  const { decision, save } = useConsent()
  const [analytics, setAnalytics] = useState(decision?.analytics ?? false)
  const [marketing, setMarketing] = useState(decision?.marketing ?? false)

  return (
    <>
      <div className="flex flex-col gap-5 py-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="consent-analytics" className="text-sm font-medium">
              Analytics
            </Label>
            <p className="text-muted-foreground text-sm leading-6">
              Helps us understand how {productName} is used so we can improve it.
            </p>
          </div>
          <Switch
            id="consent-analytics"
            checked={analytics}
            onCheckedChange={setAnalytics}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="consent-marketing" className="text-sm font-medium">
              Advertising
            </Label>
            <p className="text-muted-foreground text-sm leading-6">
              Lets us measure our campaigns and attribute referrals. Turning
              this off stops these tags from loading.
            </p>
          </div>
          <Switch
            id="consent-marketing"
            checked={marketing}
            onCheckedChange={setMarketing}
          />
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        <Button variant="ghost" onClick={() => save(CONSENT_NONE)}>
          Reject optional
        </Button>
        <Button onClick={() => save({ analytics, marketing })}>
          Save choices
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * In-product entry point (account settings). The dialog itself lives in
 * <CookieConsent /> in the root layout, so this only flips the shared open
 * state. Label is passed in because the product surface is translated.
 */
export function CookieSettingsButton({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  const consent = useContext(ConsentContext)
  if (!consent) return null
  return (
    <Button
      variant="outline"
      className={className}
      onClick={() => consent.setSettingsOpen(true)}
    >
      {children}
    </Button>
  )
}

/**
 * Footer entry point so the choice can be revisited after it is made. Renders
 * nothing outside a ConsentProvider rather than throwing, so a footer used in
 * an isolated tree (tests, embeds) still renders.
 */
export function CookieSettingsLink({ className }: { className?: string }) {
  const consent = useContext(ConsentContext)
  if (!consent) return null
  return (
    <button
      type="button"
      className={className}
      onClick={() => consent.setSettingsOpen(true)}
    >
      Cookie Settings
    </button>
  )
}
